<template>
  <div>
    <div class="mb-3">
      <div class="d-flex align-center" style="gap: 6px; margin: 0 0 8px 0">
        <h3 style="font-size: 16px; font-weight: 700; margin: 0; color: #0f3565">
          {{ $t('Resolutividade da equipe por mês ao longo dos anos') }}
        </h3>
        <v-tooltip color="white" bottom max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <v-icon small color="rgba(0,0,0,0.4)" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span class="black--text">
            <p class="mb-0 mt-2" style="font-size: 0.9rem">{{ $t("Apresenta o número de O.S.'s corretivas fechadas pela equipe interna no período, dividido pelo total de corretivas fechadas no período, sempre em relação aos equipamentos próprios.") }}</p>
          </span>
        </v-tooltip>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 12px">
        <div v-for="yearEntry in yearSeries" :key="yearEntry.year" class="d-flex align-center" style="gap: 6px">
          <div
            :style="{ background: yearEntry.color }"
            style="width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0"
          />
          <span style="color: rgba(0,0,0,0.65); font-size: 12px; font-weight: 600">
            {{ yearEntry.year }}
          </span>
        </div>
        <div v-if="goal > 0" class="d-flex align-center" style="gap: 6px">
          <div style="width: 18px; height: 2px; background: #FF69B4; border-top: 2px dashed #FF69B4; flex-shrink: 0" />
          <span style="color: rgba(0,0,0,0.65); font-size: 12px; font-weight: 600">
            {{ $t('Meta') }} ({{ goal }}%)
          </span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-center align-center" style="height: 300px">
      <v-progress-circular indeterminate color="#bbcf20" size="40" />
    </div>

    <div v-else-if="isEmpty" class="d-flex flex-column justify-center align-center" style="height: 300px">
      <v-icon color="rgba(0,0,0,0.2)" size="48">mdi-chart-bar</v-icon>
      <span style="color: rgba(0,0,0,0.4); font-size: 13px; margin-top: 8px">
        {{ $t('Nenhum dado encontrado') }}
      </span>
    </div>

    <v-chart
      v-else
      :option="chartOption"
      :autoresize="true"
      style="height: 320px; width: 100%"
    />
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
} from 'echarts/components'
import ApiService from '../../../../services/ApiService'

use([CanvasRenderer, BarChart, LineChart, TooltipComponent, GridComponent, LegendComponent, MarkLineComponent])

const YEAR_COLORS = ['#0F3565', '#BBCF20', '#2A8EA4']

export default {
  name: 'TeamResolutivityByMonth',

  components: { VChart },

  props: {
    filterProp: { type: Object, default: () => ({}) },
  },

  data: () => ({
    loading: false,
    yearSeries: [],
    goal: 0,
    chartOption: {},
    apiService: new ApiService(),
  }),

  computed: {
    isEmpty() {
      return !this.chartOption.series || this.chartOption.series.length === 0
    },
  },

  watch: {
    filterProp: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  created() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const response = await this.apiService.post(
          'dashboardBI/team-resolutivity-by-month',
          this.filterProp
        )
        const { data, goal } = response.content
        this.goal = goal || 0
        this.buildChart(data || [])
      } catch (err) {
        // errors handled globally by ApiService
      } finally {
        this.loading = false
      }
    },

    buildChart(data) {
      if (!data.length) {
        this.yearSeries = []
        this.chartOption = {}
        return
      }

      const months = data[0].months.map((m) => m.label)

      this.yearSeries = data.map((yearData, i) => ({
        year: yearData.year,
        color: YEAR_COLORS[i % YEAR_COLORS.length],
      }))

      const barSeries = data.map((yearData, i) => ({
        name: String(yearData.year),
        type: 'bar',
        data: yearData.months.map((m) => m.value ?? null),
        barMaxWidth: 24,
        itemStyle: {
          color: YEAR_COLORS[i % YEAR_COLORS.length],
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: 'top',
          color: 'rgba(0,0,0,0.65)',
          fontSize: 10,
          formatter: ({ value }) => (value !== null && value > 0 ? `${value}%` : ''),
        },
      }))

      const goalMarkLine =
        this.goal > 0
          ? {
              name: 'Meta',
              type: 'line',
              data: [],
              markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: { type: 'dashed', color: '#FF69B4', width: 2 },
                label: {
                  show: true,
                  position: 'end',
                  color: '#FF69B4',
                  fontSize: 11,
                  formatter: () => `${this.goal}%`,
                },
                data: [{ yAxis: this.goal }],
              },
            }
          : null

      const series = goalMarkLine ? [...barSeries, goalMarkLine] : barSeries

      this.chartOption = {
        animationDuration: 600,
        animationEasing: 'cubicOut',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10,30,60,0.92)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#fff', fontSize: 12 },
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const month = params[0].axisValue
            const lines = params
              .filter((p) => p.seriesType === 'bar')
              .map(
                (p) =>
                  `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:5px"></span>${p.seriesName}: <b>${p.value !== null ? p.value + '%' : '-'}</b>`
              )
              .join('<br/>')
            return `<b>${month}</b><br/>${lines}`
          },
        },
        grid: { top: 16, right: 60, bottom: 32, left: 16, containLabel: true },
        xAxis: {
          type: 'category',
          data: months,
          axisLabel: { color: 'rgba(0,0,0,0.6)', fontSize: 11 },
          axisLine: { lineStyle: { color: 'rgba(0,0,0,0.15)' } },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 100,
          axisLabel: {
            color: 'rgba(0,0,0,0.6)',
            fontSize: 11,
            formatter: (v) => `${v}%`,
          },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: 'rgba(0,0,0,0.08)' } },
        },
        series,
      }
    },
  },
}
</script>
